<template>
  <div class="committee-management-page">
    <section class="section">
      <div class="container">
        <!-- Header -->
        <div class="page-header mb-5">
          <div class="is-flex is-justify-content-space-between is-align-items-center is-flex-wrap-wrap" style="gap: 1rem;">
            <div>
              <h1 class="title is-4 mb-2">
                <span class="icon-text">
                  <span class="icon has-text-grey-dark">
                    <i class="fas fa-users-cog"></i>
                  </span>
                  <span>Gestión de Comisiones Precalificadoras</span>
                </span>
              </h1>
              <p class="subtitle is-6 has-text-grey" style="margin-bottom: 0;">
                Configuración centralizada de comisiones por departamento
              </p>
            </div>
            <div>
              <b-field label="Año del Proceso" label-position="on-border">
                <b-select v-model="selectedYear" @input="loadDepartments" :disabled="isLoading">
                  <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
                </b-select>
              </b-field>
            </div>
          </div>
        </div>

        <!-- Información -->
        <div class="info-card mb-5">
          <div class="info-card-header">
            <span class="icon has-text-grey mr-2">
              <i class="fas fa-info-circle"></i>
            </span>
            <span class="has-text-weight-semibold">Estructura de la Comisión Precalificadora</span>
          </div>
          <div class="info-card-body">
            <div class="columns is-mobile is-multiline">
              <div class="column is-12-mobile is-4-tablet">
                <div class="info-item">
                  <span class="info-badge">1</span>
                  <div>
                    <strong>Director</strong>
                    <p class="is-size-7 has-text-grey">Asignación automática del departamento</p>
                  </div>
                </div>
              </div>
              <div class="column is-12-mobile is-4-tablet">
                <div class="info-item">
                  <span class="info-badge">2</span>
                  <div>
                    <strong>Par Interno</strong>
                    <p class="is-size-7 has-text-grey">Miembro del mismo departamento</p>
                  </div>
                </div>
              </div>
              <div class="column is-12-mobile is-4-tablet">
                <div class="info-item">
                  <span class="info-badge">3</span>
                  <div>
                    <strong>Par Externo</strong>
                    <p class="is-size-7 has-text-grey">Cualquier usuario de SIGA</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Estadísticas -->
        <div class="stats-container mb-5">
          <div class="columns is-variable is-2">
            <div class="column">
              <div class="stat-card">
                <div class="stat-icon">
                  <i class="fas fa-building"></i>
                </div>
                <div class="stat-content">
                  <span class="stat-value">{{ totalDepartments }}</span>
                  <span class="stat-label">Total Departamentos</span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="stat-card stat-configured">
                <div class="stat-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="stat-content">
                  <span class="stat-value">{{ departmentsWithCommittee }}</span>
                  <span class="stat-label">Configurados</span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="stat-card stat-pending">
                <div class="stat-icon">
                  <i class="fas fa-clock"></i>
                </div>
                <div class="stat-content">
                  <span class="stat-value">{{ departmentsWithoutCommittee }}</span>
                  <span class="stat-label">Pendientes</span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="stat-card">
                <div class="stat-icon">
                  <i class="fas fa-chart-pie"></i>
                </div>
                <div class="stat-content">
                  <span class="stat-value">{{ coveragePercentage }}<small>%</small></span>
                  <span class="stat-label">Cobertura</span>
                </div>
                <div class="stat-progress">
                  <div class="stat-progress-bar" :style="{ width: coveragePercentage + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filtro de búsqueda -->
        <div class="box filter-box mb-5">
          <div class="columns is-vcentered">
            <div class="column">
              <b-field>
                <b-input
                  v-model="searchQuery"
                  placeholder="Buscar departamento por nombre..."
                  icon="search"
                  @input="filterDepartments"
                ></b-input>
              </b-field>
            </div>
            <div class="column is-narrow">
              <b-field>
                <b-select v-model="statusFilter" @input="filterDepartments" icon="filter">
                  <option value="all">Todos los estados</option>
                  <option value="with_committee">Configurados</option>
                  <option value="without_committee">Pendientes</option>
                </b-select>
              </b-field>
            </div>
          </div>
        </div>

        <!-- Tabla de Departamentos -->
        <div class="box table-box">
          <b-table
            :data="filteredDepartments"
            :loading="isLoading"
            hoverable
            :mobile-cards="true"
            paginated
            :per-page="15"
            pagination-position="bottom"
            pagination-simple
            default-sort="unit_name"
          >
            <b-table-column field="unit_name" label="Departamento" sortable v-slot="props">
              <div class="department-cell">
                <span class="department-name">{{ props.row.unit_name }}</span>
                <span class="department-type">{{ props.row.unit_type }}</span>
              </div>
            </b-table-column>

            <b-table-column field="has_committee" label="Estado" centered sortable width="140" v-slot="props">
              <span :class="['status-badge', props.row.has_committee ? 'status-configured' : 'status-pending']">
                <span class="status-dot"></span>
                {{ props.row.has_committee ? 'Configurado' : 'Pendiente' }}
              </span>
            </b-table-column>

            <b-table-column label="Miembros de la Comisión" v-slot="props">
              <div v-if="props.row.has_committee && props.row.committee" class="members-list">
                <div class="member-item">
                  <span class="member-role">Director:</span>
                  <span class="member-name">{{ (props.row.committee.director && props.row.committee.director.name) || 'N/A' }}</span>
                </div>
                <div class="member-item">
                  <span class="member-role">Par Interno:</span>
                  <span class="member-name">{{ (props.row.committee.member_2 && props.row.committee.member_2.name) || 'N/A' }}</span>
                </div>
                <div class="member-item">
                  <span class="member-role">Par Externo:</span>
                  <span class="member-name">{{ (props.row.committee.member_3 && props.row.committee.member_3.name) || 'N/A' }}</span>
                </div>
              </div>
              <span v-else class="no-committee">Sin comisión asignada</span>
            </b-table-column>

            <b-table-column label="Acciones" centered width="120" v-slot="props">
              <b-button
                v-if="props.row.has_committee"
                type="is-light"
                size="is-small"
                icon-left="edit"
                @click="openEditModal(props.row)"
              >
                Editar
              </b-button>
              <b-button
                v-else
                type="is-primary"
                size="is-small"
                icon-left="plus"
                @click="openCreateModal(props.row)"
              >
                Configurar
              </b-button>
            </b-table-column>

            <template #empty>
              <div class="empty-state">
                <span class="icon is-large has-text-grey-lighter">
                  <i class="fas fa-folder-open fa-3x"></i>
                </span>
                <p class="has-text-grey mt-4">No se encontraron departamentos</p>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import CommitteeConfigModal from './CommitteeConfigModal.vue';

export default {
  name: 'CommitteeManagementPage',

  data() {
    const currentYear = new Date().getFullYear();
    return {
      selectedYear: currentYear,
      availableYears: [currentYear - 1, currentYear, currentYear + 1],
      departments: [],
      filteredDepartments: [],
      searchQuery: '',
      statusFilter: 'all',
      isLoading: false
    };
  },

  computed: {
    totalDepartments() {
      return this.departments.length;
    },

    departmentsWithCommittee() {
      return this.departments.filter(d => d.has_committee).length;
    },

    departmentsWithoutCommittee() {
      return this.departments.filter(d => !d.has_committee).length;
    },

    coveragePercentage() {
      if (this.totalDepartments === 0) return 0;
      return Math.round((this.departmentsWithCommittee / this.totalDepartments) * 100);
    }
  },

  mounted() {
    this.loadDepartments();
  },

  methods: {
    async loadDepartments() {
      this.isLoading = true;
      try {
        const response = await axios.get('/committee_members', {
          params: { year: this.selectedYear }
        });
        
        if (response.data.success) {
          this.departments = response.data.departments || [];
          this.filterDepartments();
        }
      } catch (error) {
        console.error('Error loading departments:', error);
        this.$buefy.toast.open({
          message: 'Error al cargar los departamentos',
          type: 'is-danger',
          duration: 3000
        });
      } finally {
        this.isLoading = false;
      }
    },

    filterDepartments() {
      let filtered = [...this.departments];

      // Filtrar por texto
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(d => 
          d.unit_name.toLowerCase().includes(query)
        );
      }

      // Filtrar por estado
      if (this.statusFilter === 'with_committee') {
        filtered = filtered.filter(d => d.has_committee);
      } else if (this.statusFilter === 'without_committee') {
        filtered = filtered.filter(d => !d.has_committee);
      }

      this.filteredDepartments = filtered;
    },

    openCreateModal(department) {
      this.$buefy.modal.open({
        parent: this,
        component: CommitteeConfigModal,
        props: {
          department: department,
          year: this.selectedYear,
          existingCommittee: null
        },
        hasModalCard: true,
        trapFocus: true,
        canCancel: ['escape', 'x'],
        events: {
          'committee-saved': () => {
            this.loadDepartments();
          }
        }
      });
    },

    openEditModal(department) {
      this.$buefy.modal.open({
        parent: this,
        component: CommitteeConfigModal,
        props: {
          department: department,
          year: this.selectedYear,
          existingCommittee: department.committee
        },
        hasModalCard: true,
        trapFocus: true,
        canCancel: ['escape', 'x'],
        events: {
          'committee-saved': () => {
            this.loadDepartments();
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.committee-management-page {
  background: #fafafa;
  min-height: calc(100vh - 52px);
}

/* Header */
.page-header {
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* Info Card */
.info-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.info-card-header {
  background: #f8f9fa;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
}

.info-card-body {
  padding: 1rem 1.25rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.info-badge {
  width: 24px;
  height: 24px;
  background: #363636;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

/* Stats */
.stats-container {
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
}

.stat-icon {
  width: 48px;
  height: 48px;
  background: #f5f5f5;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 1.25rem;
}

.stat-configured .stat-icon {
  background: #e8f5e9;
  color: #2e7d32;
}

.stat-pending .stat-icon {
  background: #fff3e0;
  color: #ef6c00;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #363636;
  line-height: 1;
}

.stat-value small {
  font-size: 1rem;
  font-weight: 600;
}

.stat-label {
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;
}

.stat-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #eee;
}

.stat-progress-bar {
  height: 100%;
  background: #363636;
  transition: width 0.3s ease;
}

/* Filter Box */
.filter-box {
  padding: 1rem 1.25rem;
}

/* Table Box */
.table-box {
  padding: 0;
  overflow: hidden;
}

.table-box :deep(.b-table) {
  border-radius: 8px;
}

.table-box :deep(table) {
  border-collapse: collapse;
}

.table-box :deep(th) {
  background: #fafafa !important;
  border-bottom: 2px solid #eee !important;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  padding: 1rem !important;
}

.table-box :deep(td) {
  padding: 1rem !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f0f0f0 !important;
}

.table-box :deep(tr:hover) {
  background: #fafafa !important;
}

/* Department Cell */
.department-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.department-name {
  font-weight: 600;
  color: #363636;
}

.department-type {
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-configured {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-configured .status-dot {
  background: #4caf50;
}

.status-pending {
  background: #fff3e0;
  color: #e65100;
}

.status-pending .status-dot {
  background: #ff9800;
}

/* Members List */
.members-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.member-role {
  color: #888;
  min-width: 85px;
}

.member-name {
  color: #363636;
}

.no-committee {
  color: #999;
  font-size: 0.85rem;
  font-style: italic;
}

/* Empty State */
.empty-state {
  padding: 4rem 2rem;
  text-align: center;
}

/* Box Override */
.box {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  border: none;
}

/* Button styles */
:deep(.button.is-light) {
  background: #f5f5f5;
  border: none;
}

:deep(.button.is-light:hover) {
  background: #eee;
}

:deep(.button.is-primary) {
  background: #363636;
  border: none;
}

:deep(.button.is-primary:hover) {
  background: #4a4a4a;
}
</style>
